import {Workspace} from '@innowise-group/core';
import {Elements} from 'react-flow-renderer';
import {WorkspaceElementData} from '../components/workspace-view';
import {updateWorkspaceData, UpdatedWorkspaceDataResult} from './update-workspace-data.utility';

type AbortWorkspacePayload = Omit<UpdatedWorkspaceDataResult, 'booked_status' | 'active_requests'>;

const normalizeId = (id: string | number | null | undefined): string => String(id ?? '');

const bookingFingerprint = (workspace: Workspace) =>
  (workspace.bookedStatus || []).map((booking) => ({
    employeeId: booking.employee?.id ?? null,
    start: booking.range?.startDay ?? null,
    end: booking.range?.endDay ?? null,
    comment: booking.comment ?? null,
  }));

/** Comparable snapshot of fields that Cancel must restore on the server. */
export const getWorkspaceAbortFingerprint = (workspace: Workspace): string =>
  JSON.stringify({
    status: workspace.status,
    employeeId: workspace.employee?.id ?? null,
    number: workspace.number ?? null,
    roomId: workspace.room?.id ?? null,
    rotateDegree: workspace.rotateDegree ?? 0,
    coordinates: workspace.coordinates ?? null,
    comment: workspace.comment ?? null,
    rangeStart: workspace.range?.startDay ?? null,
    rangeEnd: workspace.range?.endDay ?? null,
    booked: bookingFingerprint(workspace),
  });

export const toAbortUpdatePayload = (workspace: Workspace): AbortWorkspacePayload => {
  const {booked_status: _booked, active_requests: _active, ...payload} = updateWorkspaceData(workspace, true);
  return payload;
};

export interface BuildAbortWorkspaceUpdatesArgs {
  initialWorkspaces: Elements<WorkspaceElementData>;
  currentWorkspaces: Elements<WorkspaceElementData>;
  workspacesToRevert?: Workspace[];
}

/**
 * Only desks that differ from the edit-mode baseline (plus explicit revert list).
 * Avoids PATCHing the entire floor on Cancel.
 */
export const buildAbortWorkspaceUpdates = ({
  initialWorkspaces,
  currentWorkspaces,
  workspacesToRevert = [],
}: BuildAbortWorkspaceUpdatesArgs): AbortWorkspacePayload[] => {
  const currentById = new Map(
    currentWorkspaces
      .filter((element) => !element.data?.isDraft)
      .map((element) => [normalizeId(element.data.id), element.data]),
  );

  const updatesById = new Map<string, AbortWorkspacePayload>();

  initialWorkspaces.forEach((element) => {
    const initial = element.data;
    if (!initial?.id) {
      return;
    }
    const id = normalizeId(initial.id);
    const current = currentById.get(id);
    // Deleted desks are re-created via multiple-create, not update.
    if (!current) {
      return;
    }
    if (getWorkspaceAbortFingerprint(initial) !== getWorkspaceAbortFingerprint(current)) {
      updatesById.set(id, toAbortUpdatePayload(initial));
    }
  });

  workspacesToRevert.forEach((workspace) => {
    if (!workspace?.id) {
      return;
    }
    updatesById.set(normalizeId(workspace.id), toAbortUpdatePayload(workspace));
  });

  return Array.from(updatesById.values());
};
